import React from 'react';
import styled from 'styled-components';
import { FiInfo } from 'react-icons/fi';
import { colors, typography } from '../../theme/colors';
import { formatAmount } from '../../utils/documentNumbering';

// Rounded card representing one document on the cycle canvas. Pure
// presentation — selection, hover, and click handlers come from the parent
// canvas.
const CycleNode = ({
  node,
  width,
  height,
  selected,
  highlighted,
  dimmed,
  onClick,
  onAuditClick
}) => {
  if (!node) return null;
  const isInvoice = ['PI', 'NPI', 'IPI', 'SI', 'NSI'].includes(node.tranTypeCode);
  const paidPct = isInvoice && node.totalAmount > 0
    ? Math.min(100, Math.round((node.paidAmount / node.totalAmount) * 100))
    : null;

  return (
    <Card
      $width={width}
      $height={height}
      $selected={selected}
      $highlighted={highlighted}
      $dimmed={dimmed}
      $status={node.status}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(node); }}
    >
      <TopRow>
        <Code>{node.tranTypeCode}</Code>
        <DocNum title={node.docNumber}>{node.docNumber}</DocNum>
        <AuditBtn
          onClick={(e) => { e.stopPropagation(); onAuditClick && onAuditClick(node, e.currentTarget); }}
          title="Who created / approved?"
        >
          <FiInfo size={11} />
        </AuditBtn>
      </TopRow>
      <Party title={node.partyName}>{node.partyName || '—'}</Party>
      <Total>{formatAmount(node.totalAmount)}</Total>
      <BottomRow>
        <DateText>{node.docDate}</DateText>
        <StatusPill $status={node.status}>{node.status}</StatusPill>
      </BottomRow>
      {paidPct !== null && (
        <ProgressTrack>
          <ProgressFill style={{ width: `${paidPct}%` }} $full={paidPct >= 100} />
        </ProgressTrack>
      )}
    </Card>
  );
};

export default CycleNode;

// ── Status → border color ───────────────────────────────────────────────────
const statusColor = (status) => {
  switch (status) {
    case 'Posted':
    case 'Paid':
    case 'Completed':
    case 'Received':
    case 'Approved':
    case 'Delivered':
    case 'Closed':
      return colors.success.main;
    case 'Partial':
    case 'Partially Received':
    case 'Partially Delivered':
    case 'Sent':
    case 'Submitted':
      return colors.warning.main;
    case 'Cancelled':
    case 'Rejected':
    case 'Expired':
      return colors.error.main;
    case 'Draft':
    default:
      return colors.text.tertiary;
  }
};

const Card = styled.div`
  position: absolute;
  width: ${p => p.$width}px;
  height: ${p => p.$height}px;
  background: white;
  border: 1.5px solid ${p => p.$selected ? colors.primary.main : statusColor(p.$status)};
  border-radius: 6px;
  padding: 6px 8px 4px 8px;
  box-shadow: ${p => p.$selected
    ? `0 0 0 3px ${colors.primary.main}30, 0 4px 12px rgba(0,0,0,0.12)`
    : '0 1px 3px rgba(0,0,0,0.08)'};
  cursor: pointer;
  transition: all 0.18s ease;
  opacity: ${p => p.$dimmed ? 0.25 : 1};
  outline: ${p => p.$highlighted ? `2px solid ${colors.primary.main}` : 'none'};
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: ${typography.fontSize.xs};
  z-index: ${p => p.$selected ? 5 : (p.$highlighted ? 3 : 2)};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 4;
  }
`;

const TopRow = styled.div`
  display: flex; align-items: center; gap: 5px;
`;
const Code = styled.span`
  font-size: 11px; font-weight: 700; letter-spacing: 0.4px;
  padding: 2px 6px; border-radius: 3px;
  background: ${colors.primary.main}18; color: ${colors.primary.main};
`;
const DocNum = styled.span`
  flex: 1;
  font-weight: 600;
  font-size: 13px;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const AuditBtn = styled.button`
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${colors.text.secondary};
  display: inline-flex;
  &:hover { color: ${colors.primary.main}; }
`;
const Party = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
`;
const Total = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily.mono};
  margin-top: 2px;
`;
const BottomRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-top: auto;
  font-size: 11px;
  gap: 6px;
`;
const DateText = styled.span`
  color: ${colors.text.secondary};
  font-family: ${typography.fontFamily.mono};
  font-weight: 500;
`;
const StatusPill = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 9px;
  color: ${p => statusColor(p.$status)};
  background: ${p => statusColor(p.$status)}1a;
`;
const ProgressTrack = styled.div`
  height: 3px;
  background: ${colors.neutral?.[100] || '#e5e7eb'};
  border-radius: 2px;
  margin-top: 2px;
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  background: ${p => p.$full ? colors.success.main : colors.warning.main};
  transition: width 0.3s ease;
`;
